import React, { useEffect, useState } from 'react'

import styled from 'styled-components'
import DownloadIcon from '@mui/icons-material/DownloadOutlined'

import { Panel } from 'components/Panel'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import Table from 'components/Table'

import ComponentList from 'pages/Device/SystemComponentsPanel/ComponentList'
import LastUpdated from 'pages/Device/LastUpdated'

import useNexiaStream from 'hooks/useNexiaStream'
import useScreenSize from 'hooks/useScreenSize'

import { NoData, PanelHeaderSecondaryButton } from 'styles/shared'

import Apptentive from 'helpers/apptentive'
import RunReportModal from 'pages/Device/SystemComponentsPanel/RunReportModal'
import { color } from 'styles/colors'

export type Props = {
  deviceId: string
  reportCache: object
  readOnly: boolean
  communicating: boolean
}

const SYSTEM_COMPONENTS = 'System Components'

const SystemComponentsPanel = ({ deviceId, communicating, reportCache, readOnly }: Props) => {
  const screenSize = useScreenSize()

  const { eventData: report } = useNexiaStream(deviceId, 'sys_components', reportCache)
  const [totalComponents, setTotalComponents] = useState(0)
  const isLoaded = report !== null
  const [isRunReportOpen, setIsRunReportOpen] = useState(false)

  useEffect(() => {
    if (report) {
      setTotalComponents((report.components || []).length)
      sendApptentiveEvent()
    }
  }, [report]) // eslint-disable-line react-hooks/exhaustive-deps

  const sendApptentiveEvent = () => {
    let hasRelayPanel = false
    const components = (report.components || [])

    components.forEach((component) => {
      if (component.isRelayPanel === true) {
        hasRelayPanel = true
      }
    })

    if (hasRelayPanel && communicating) {
      Apptentive.sendEvent('viewed_hybrid_system_details')
    } else if (communicating) {
      Apptentive.sendEvent('viewed_communicating_system_details')
    } else {
      Apptentive.sendEvent('viewed_24v_system_details')
    }
  }

  const onUpdate = (componentsCount: number) => {
    setTotalComponents(componentsCount)
  }

  return (
    <S.Panel pingdomId="sys-components-container" id="system-components" scrollToSection>
      <S.PanelHeader>
        <S.PanelHeaderLeft>
          <Panel.Title>{totalComponents && screenSize !== 'small' ? SYSTEM_COMPONENTS + ' (' + `${totalComponents}` + ')' : SYSTEM_COMPONENTS}</Panel.Title>
          <Button decoration="primary" styles={PanelHeaderSecondaryButton} onPress={() => setIsRunReportOpen(true)}><S.ButtonText><S.DownloadIcon /><span>Warranty Report</span></S.ButtonText></Button>
        </S.PanelHeaderLeft>
        <RunReportModal
          deviceId={deviceId}
          isOpen={isRunReportOpen}
          onClose={() => setIsRunReportOpen(false)}
        />
        {report && <LastUpdated date={report.lastUpdatedAt} />}
      </S.PanelHeader>
      {
        !isLoaded
          ? <S.NoData><h1>No Data Received Yet</h1></S.NoData>
          : (
            <S.Content>
              <Table.Header>
                <Table.HeaderCell title="Description" />
                <Table.HeaderCell title="Model Number" />
                <Table.HeaderCell title="Serial Number" />
                <Table.HeaderCell title="Version Number" />
                <Table.HeaderCell title="Device Reporting Status" tooltip="Identifies if a device is currently reporting on the communication bus" />
                <Table.HeaderCell title="Warranty Status" />
              </Table.Header>
              <ComponentList components={report.components || []} deviceId={deviceId} onUpdateCallBack={onUpdate} readOnly={readOnly} />
            </S.Content>
          )
      }
    </S.Panel>
  )
}

export default SystemComponentsPanel

const S = {
  Content: styled.div`
    overflow-wrap: anywhere;
  `,
  NoData: styled(NoData)`
    h1 {
      font-size: 28px;
    }
  `,
  Panel: styled(Panel)`
    scroll-margin-top: 60px;
  `,
  PanelHeaderLeft: styled(Panel.Header.Left)`
    display: flex;
    align-items: center;
  `,
  PanelHeader: styled(Panel.Header)`
    padding-right: 4px;
  `,
  ButtonText: styled.div`
    color: ${color('primaryButton')};
    margin-bottom: -8px;
    font-weight: 700;
    font-size: 16px;
    display: flex;
    align-items: center;
    gap: 5px;
    &:hover {
      color: ${color('primaryButtonHover')};
    }
  `,
  DownloadIcon: styled(DownloadIcon)`
    font-size: 1.25em;
  `
}
